import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer
} from "recharts";
import { useState, useEffect } from "react";
import "./Charts.css";
import { handleClickLogic } from "../../utils/filterFunctions";
import CustomBarLabel from "./CustomBarLabel";

const VerticalBarChartComparison = ({ showData, xKey, title, selectedKey, frozenKey, onBarClick, height = 250 }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isFrozen, setIsFrozen] = useState(false);
  const [frozenData, setFrozenData] = useState(null);

  const dataToRender = isFrozen ? frozenData : showData;

  useEffect(() => {
    setSelectedCategory(selectedKey);
  }, [selectedKey]);

  useEffect(() => {
    if (frozenKey !== title) {
      setIsFrozen(false);
      setFrozenData(null);
    }
  }, [frozenKey, title]);

  const hasData = Array.isArray(dataToRender) && dataToRender.length > 0;

  if (!hasData) {
    return (
      <div className="chart-card">
        <h3>{title}</h3>
        <div className="chart-body empty">
          <p style={{ textAlign: "center", color: "#888", margin: "20px 0" }}>
            Sin datos disponibles
          </p>
        </div>
      </div>
    );
  }

  const colors = ['#00BFFF', '#FFA500'];
  const barKeys = Object.keys(dataToRender[0]).filter(key => key !== xKey);

  const handleBarClick = (data) => {
    handleClickLogic({
      data,
      xKey,
      selectedCategory,
      isFrozen,
      showData,
      setIsFrozen,
      setFrozenData,
      setSelectedCategory,
      onBarClick,
    });
  };

  const CustomTick = ({ x, y, payload, total }) => {
    const label = payload.value || "";
    const length = label.length;

    const getBaseFontSize = () => {
      if (total > 35) return 5;
      if (total > 25) return 6;
      if (total > 15) return 7;
      if (total > 10) return 8;
      return 9;
    };

    const adjustForLength = (base) => {
      if (length > 40) return base - 3;
      if (length > 25) return base - 2;
      if (length > 15) return base - 1;
      return base;
    };

    const baseFont = getBaseFontSize();
    const finalFont = Math.max(adjustForLength(baseFont), 6);

    const shouldRotate = total > 1;

    const adjustedX = x - (shouldRotate ? 76 : 80);

    const adjustedY = y - 8;

    return (
      <foreignObject
        x={adjustedX}
        y={adjustedY}
        width={shouldRotate ? 70 : 80}
        height={shouldRotate ? 60 : 45}
        style={{ overflow: "visible" }}
      >
        <div
          style={{
            fontSize: `${finalFont}px`,
            textAlign: "right",
            lineHeight: "1.1",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
            transform: shouldRotate ? "rotate(-40deg)" : "none",
            transformOrigin: "right top",
          }}
        >
          {label}
        </div>
      </foreignObject>
    );
  };

  return (
    <div className="chart-card">
      <h3>{title}</h3>
      <div className="chart-body">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dataToRender} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey={xKey} type="category" interval={0} tick={(props) => (
              <CustomTick {...props} total={dataToRender.length} />
            )} />
            <Tooltip />
            <Legend />
            {barKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colors[index % colors.length]}
                onClick={handleBarClick}
                shape={(props) => {
                  const category = props.payload?.[xKey];
                  const isSelected = selectedCategory === category;
                  const opacity = isFrozen
                    ? selectedCategory && !isSelected ? 0.4 : 1
                    : 1;
                  const stroke = isFrozen && isSelected ? "#333" : "none";
                  const strokeWidth = isFrozen && isSelected ? 2 : 0;
                  return (
                    <Rectangle
                      {...props}
                      fillOpacity={opacity}
                      stroke={stroke}
                      strokeWidth={strokeWidth}
                      cursor="pointer"
                    />
                  );
                }}>
                <LabelList dataKey={key} content={<CustomBarLabel chartType='horizontalBar' />} />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default VerticalBarChartComparison;